import React, { useState, useEffect, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { DisablableOption } from "@models";
import { SourceBook } from "@dndModels";

import DandDAPI from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import LoadingBox from "@components/input/LoadingBox";
import { SOURCE_BOOK_TYPES } from "@dndModels/Constants";
import { SourceBookType } from "@dndModels/Types";

const TYPE_PRIORITY_MAP = {
  "Source Book": 0,
  "Setting": 1,
  "Adventure": 2,
  "Homebrew": 3,
  "Unearthed Arcana": 4
};

interface Props {
  name?: string;
  id?: string;
  ids?: string[];
  type?: SourceBookType | "";
  types?: SourceBookType[];
  multiple?: boolean;
  disabled?: boolean;
  archives?: boolean;
  options?: DisablableOption[];
  onChange?: (type: SourceBookType, id: string) => void;
  onChangeMultiple?: (types: SourceBookType[], ids: string[]) => void;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function SelectSourceBookBox({
  name = "",
  id = "",
  ids = [],
  type,
  types,
  multiple: multipleProp,
  disabled = false,
  archives,
  options,
  onChange,
  onChangeMultiple
}: Props) {
  // Derive whether we're in multiple mode: explicit prop > ids array prop > id prop
  const isMultiple = multipleProp ?? (ids !== undefined && onChangeMultiple !== undefined);

  // Whether type/types are controlled externally or managed internally
  const typeControlled = type !== undefined || types !== undefined;

  const [fetched, setFetched] = useState(false);
  const [run_filter, setRunFilter] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [is_archived, setIsArchived] = useState(archives === undefined ? false : archives);
  const [source_books, setSourceBooks] = useState<SourceBook[]>([]);
  const [available_types, setAvailableTypes] = useState<string[]>([]);
  const [prev_types, setPrevTypes] = useState<SourceBookType[]>([]);
  const [filtered, setFiltered] = useState<DisablableOption[]>([]);

  // Internal type state used only when type/types are not controlled by the parent
  const [internalType, setInternalType] = useState<SourceBookType | "">("");
  const [internalTypes, setInternalTypes] = useState<SourceBookType[]>([]);

  // Resolved type values: prefer controlled props, fall back to internal state
  const resolvedType = typeControlled ? type : internalType;
  const resolvedTypes = typeControlled ? types : internalTypes;

  const load = useCallback(async () => {
    let sbs = api.source_books;
    let ts: string[] = [];
    if (options) {
      const sb_ids = options.map(o => o.id);
      sbs = sbs.filter(o => sb_ids.includes(o._id));
    }
    sbs = sbs.sort((a, b) => {
      if (a.source_book_type === b.source_book_type) {
        return a.name.localeCompare(b.name);
      } else {
        return TYPE_PRIORITY_MAP[a.source_book_type] - TYPE_PRIORITY_MAP[b.source_book_type];
      }
    });
    ts = common_util.unique_array(sbs.flatMap(o => o.source_book_type));
    ts = SOURCE_BOOK_TYPES.filter(o => ts.includes(o));
    setSourceBooks(sbs);
    setAvailableTypes(ts);
    setRunFilter(true);
  }, [options]);

  const do_filter = useCallback(() => {
    let sbs = source_books;
    if (prev_types.length > 0) {
      sbs = api.filter("source_book", sbs, { source_book_type: { "in": prev_types } });
    }
    sbs = api.filter("source_book", sbs, { is_archived });
    setFiltered(sbs.map(o => {
      const opt: DisablableOption = {
        id: o._id,
        text: `${o.use_name} (${o.abbreviation})`
      };
      return opt;
    }));
  }, [source_books, prev_types, is_archived]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (run_filter) {
      setRunFilter(false);
      do_filter();
    }
  }, [run_filter, do_filter]);

  useEffect(() => {
    if (isMultiple) {
      const effective = resolvedTypes ?? [];
      if (!common_util.listsMatch(effective, prev_types)) {
        setPrevTypes(effective);
        setRunFilter(true);
      }
    } else {
      if (resolvedType) {
        if (prev_types.length !== 1 || prev_types[0] !== resolvedType) {
          setPrevTypes([resolvedType as SourceBookType]);
          setRunFilter(true);
        }
      } else if (prev_types.length > 0) {
        // No type selected — clear filter so all source books show
        setPrevTypes([]);
        setRunFilter(true);
      }
    }
  }, [resolvedType, resolvedTypes, isMultiple]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  if (!fetched) {
    return <LoadingBox />;
  }

  if (isMultiple && onChangeMultiple) {
    return (
      <Grid container spacing={0} direction="row">
        {name !== "" &&
          <Grid className="centered header" size={{ xs: 4 }}>
            {name}
          </Grid>
        }
        {available_types.length > 1 &&
          <Grid size={{ xs: name === "" ? 6 : 4 }}>
            <SelectStringBox
              name="Source Types"
              disabled={disabled}
              values={resolvedTypes}
              options={available_types}
              multiple
              onChange={(changed: SourceBookType[]) => {
                setReloading(true);
                if (typeControlled) {
                  onChangeMultiple(changed, ids);
                } else {
                  setInternalTypes(changed);
                }
              }}
            />
          </Grid>
        }
        <Grid size={{ xs: name === "" ? (available_types.length > 1 ? 6 : 12) : (available_types.length > 1 ? 4 : 8) }}>
          {!reloading &&
            <SelectStringBox
              name="Source Book"
              values={ids}
              options={filtered}
              disabled={disabled}
              multiple
              onChange={(changed: string[]) => {
                onChangeMultiple(resolvedTypes ?? [], changed);
              }}
            />
          }
        </Grid>
        {archives === undefined &&
          <Grid size={{ xs: 12 }}>
            <CheckBox
              name="Check Archives"
              value={is_archived}
              onChange={(changed: boolean) => {
                setIsArchived(changed);
                setReloading(true);
              }}
            />
          </Grid>
        }
      </Grid>
    );
  }

  if (!isMultiple && onChange) {
    return (
      <Grid container spacing={0} direction="row">
        {name !== "" &&
          <Grid className="centered header" size={{ xs: 4 }}>
            {name}
          </Grid>
        }
        {available_types.length > 1 &&
          <Grid size={{ xs: name === "" ? 6 : 4 }}>
            <SelectStringBox
              name="Source Type"
              disabled={disabled}
              value={resolvedType}
              options={available_types}
              onChange={(changed: SourceBookType) => {
                setReloading(true);
                if (typeControlled) {
                  onChange(changed, id);
                } else {
                  setInternalType(changed);
                }
              }}
            />
          </Grid>
        }
        <Grid size={{ xs: name === "" ? (available_types.length > 1 ? 6 : 12) : (available_types.length > 1 ? 4 : 8) }}>
          {!reloading && api.source_books &&
            <SelectStringBox
              name="Source Book"
              value={id}
              options={filtered}
              disabled={disabled}
              onChange={(changed: string) => {
                onChange(resolvedType as SourceBookType ?? prev_types[0], changed);
              }}
            />
          }
        </Grid>
        {archives === undefined &&
          <Grid size={{ xs: 12 }}>
            <CheckBox
              name="Check Archives"
              value={is_archived}
              onChange={setIsArchived}
            />
          </Grid>
        }
      </Grid>
    );
  }

  return <></>;
}